import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { refreshSession, type User } from '@/features/auth';

type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

type AuthState = {
  status: AuthStatus;
  user: User | null;
  setSession: (user: User) => void;
  clearSession: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('unknown');
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let active = true;
    refreshSession()
      .then((profile) => {
        if (!active) return;
        setUser(profile);
        setStatus('authenticated');
      })
      .catch(() => {
        if (active) setStatus('unauthenticated');
      });
    return () => {
      active = false;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      setSession: (u) => {
        setUser(u);
        setStatus('authenticated');
      },
      clearSession: () => {
        setUser(null);
        setStatus('unauthenticated');
      },
    }),
    [status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** The signed-in Grower, or `null` — never throws, for a caller that only wants a name if one exists. */
export function useOptionalUser(): User | null {
  return useContext(AuthContext)?.user ?? null;
}
